import * as Sentry from '@sentry/react'
import { Toaster as Sonner } from '@/components/ui/sonner'
import { Toaster } from '@/components/ui/toaster'
import { TooltipProvider } from '@/components/ui/tooltip'
import { useAuthListener } from '@/hooks/useAuthListener'
import { useCabasSyncListener } from '@/hooks/useCabasSyncListener'
import { queryClient } from '@/integrations/react-query'
import { store } from '@/store'
import { useAppSelector } from '@/store/hooks'
import { QueryClientProvider } from '@tanstack/react-query'
import { Provider } from 'react-redux'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'

import Auth from './pages/Auth'
import CabasSync from './pages/CabasSync'
import CabasSyncDetail from './pages/CabasSyncDetail'
import CarDetail from './pages/CarDetail'
import Cars from './pages/Cars'
import CaseDetail from './pages/CaseDetail'
import Cases from './pages/Cases'
import InvoiceDetail from './pages/InvoiceDetail'
import Invoices from './pages/Invoices'
import NotFound from './pages/NotFound'
import EstimateFeedback from './pages/EstimateFeedback'
import EstimateFeedbacks from './pages/EstimateFeedbacks'

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAppSelector(state => state.auth)

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  return user ? <>{children}</> : <Navigate to="/auth" />
}

const AdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAppSelector(state => state.auth)

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/auth" />
  }

  if (!user.roles?.includes('admin')) {
    return <Navigate to="/" />
  }

  return <>{children}</>
}

const AppRoutes = () => {
  useAuthListener()
  useCabasSyncListener()

  return (
    <Routes>
      <Route path="/auth" element={<Auth />} />
      <Route path="/" element={<Navigate to="/cases" />} />
      <Route
        path="/cars"
        element={
          <ProtectedRoute>
            <Cars />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cars/:id"
        element={
          <ProtectedRoute>
            <CarDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute>
            <Invoices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices/:id"
        element={
          <ProtectedRoute>
            <InvoiceDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cases"
        element={
          <ProtectedRoute>
            <Cases />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cases/:id"
        element={
          <ProtectedRoute>
            <CaseDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/estimate-feedback"
        element={
          <ProtectedRoute>
            <EstimateFeedbacks />
          </ProtectedRoute>
        }
      />
      <Route
        path="/estimate-feedback/:id"
        element={
          <ProtectedRoute>
            <EstimateFeedback />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cabas-sync"
        element={
          <AdminRoute>
            <CabasSync />
          </AdminRoute>
        }
      />
      <Route
        path="/cabas-sync/:id"
        element={
          <AdminRoute>
            <CabasSyncDetail />
          </AdminRoute>
        }
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

const App = () => (
  <Sentry.ErrorBoundary
    fallback={
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-8 text-center">
        <h1 className="text-lg font-semibold">Något gick fel</h1>
        <p className="text-muted-foreground">Ett oväntat fel inträffade. Ladda om sidan för att försöka igen.</p>
        <button
          className="rounded-md border px-4 py-2"
          onClick={() => window.location.reload()}
        >
          Ladda om
        </button>
      </div>
    }
  >
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </Provider>
  </Sentry.ErrorBoundary>
)

export default App
